<template>
  <div class="px-12 h-auto">
    <!-- Header -->
    <header class="bg-white border-b border-gray-200 shadow-sm">
      <div class="mx-auto px-4 py-3 flex justify-between items-center">
        <h1 class="text-2xl font-semibold text-blue-900">Rafiki360 Management</h1>
        <div class="flex items-center">
          <div class="relative mr-3">
            <span
              class="absolute top-0 right-0 -mt-1 -mr-1 bg-gray-700 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
            >
              3
            </span>
            <button class="text-gray-500 hover:text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9"
                />
              </svg>
            </button>
          </div>
          <div class="flex items-center">
            <button
              @click="dropdownOpen = !dropdownOpen"
              class="relative flex items-center w-18 h-12 overflow-hidden focus:outline-none"
            >
              <img
                src="@/assets/images/avatar.png"
                alt="profile"
                class="w-10 h-10 rounded-full object-cover mr-2"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              v-if="dropdownOpen"
              @click="dropdownOpen = false"
              class="fixed inset-0 z-10 w-full h-full"
            ></div>
            <transition
              enter-active-class="transition duration-150 ease-out transform"
              enter-from-class="scale-95 opacity-0"
              enter-to-class="scale-100 opacity-100"
              leave-active-class="transition duration-150 ease-in transform"
              leave-from-class="scale-100 opacity-100"
              leave-to-class="scale-95 opacity-0"
            >
              <div
                v-if="dropdownOpen"
                class="absolute right-0 z-20 w-48 mt-16 py-2 bg-white rounded-md shadow-xl"
              >
                <button
                  class="block px-4 py-2 text-sm w-full text-gray-700 hover:bg-blue-100 hover:text-orange-400"
                  @click="redirectProfilePage"
                >
                  Profile
                </button>
                <button
                  class="block px-4 py-2 w-full text-sm text-gray-700 hover:bg-blue-100 hover:text-orange-400"
                  @click="logout"
                >
                  Log out
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </header>
    <!-- Filter and Search -->
    <div class="flex flex-wrap items-center justify-between py-4">
      <div class="flex items-center space-x-4 flex-wrap">
        <!-- Search Input -->
        <div class="relative flex-1">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search facilities"
            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          <button class="absolute left-0 top-0 h-full px-3 text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>
        </div>
      </div>
      <!-- Register Facility Button -->
      <div
        v-if="currentTab === 'billing'"
        class="flex items-center space-x-4 mt-4 md:mt-0"
      >
        <button
          type="button"
          class="mt-2 md:mt-0 flex items-center px-4 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 transition"
          @click="openNewRafikiFacilityModal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Register Facility
        </button>
      </div>
    </div>
    <!-- New Facility Modal Component -->
    <NewRafikiFacilityModal
      :show_facility_modal="showFacilityModal"
      :facility="selectedFacility"
      @close_facility_modal="closeFacilityModal"
      @facility-submitted="handleFacilitySubmit"
      @specialists-submitted="handleSpecialistsFinish"
    />
    <!-- Tabs -->
    <div class="tabs flex border-b mb-4">
      <button
        class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-orange-400 text-orange-500': currentTab === 'billing' }"
        @click="currentTab = 'billing'"
      >
        Billing
      </button>
    </div>
    <component
      :is="currentTabComponent"
      ref="tabComponent"
      :searchQuery="searchQuery"
      :filters="filters"
      @edit-facility="handleEditFacility"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import NewRafikiFacilityModal from '@/components/forms/NewRafikiFacilityModal.vue';
import BillingTab from '@/components/tabs/BillingTab.vue';
import { setApiSystem } from '@/services/api';

// Debounce utility
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const dropdownOpen = ref(false);
const searchQuery = ref('');
const currentTab = ref('billing');
const showFacilityModal = ref(false);
const isDropdownOpen = ref(false);
const facilityId = ref(null);
const selectedFacility = ref(null);
const tabComponent = ref(null);
const isFetchingFacilities = ref(false); // Prevent concurrent fetches

const filters = ref({
  start_date: '',
  end_date: '',
});

onMounted(() => {
  console.log('DoctorBookingsPage mounted');
  setApiSystem('rafiki360');
  if (!store.getters.isAuthenticated('rafiki360')) {
    console.warn('User not authenticated for rafiki360, emitting login-required');
    emit('login-required');
  } else {
    triggerFetchFacilities();
  }
});

const currentTabComponent = computed(() => {
  return currentTab.value === 'billing' ? BillingTab : BillingTab;
});

const toggleDropdown = () => {
  console.log('DoctorBookingsPage: Toggling dropdown:', isDropdownOpen.value);
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdownModal = () => {
  console.log('DoctorBookingsPage: Closing dropdown');
  isDropdownOpen.value = false;
};

const applyFilters = debounce(() => {
  console.log('DoctorBookingsPage: Filters applied:', filters.value);
  closeDropdownModal();
  triggerFetchFacilities();
}, 500);

const cancelFilters = () => {
  console.log('DoctorBookingsPage: Filters cancelled');
  filters.value = { start_date: '', end_date: '' };
  closeDropdownModal();
  triggerFetchFacilities();
};

const openNewRafikiFacilityModal = () => {
  console.log('DoctorBookingsPage: Opening new facility modal');
  showFacilityModal.value = true;
  facilityId.value = null;
  selectedFacility.value = null;
};

const closeFacilityModal = () => {
  console.log('DoctorBookingsPage: Closing facility modal');
  showFacilityModal.value = false;
  facilityId.value = null;
  selectedFacility.value = null;
};

const triggerFetchFacilities = debounce(async () => {
  if (isFetchingFacilities.value) {
    console.warn('DoctorBookingsPage: Fetch facilities aborted: already fetching');
    return;
  }
  if (
    currentTab.value === 'billing' &&
    tabComponent.value &&
    typeof tabComponent.value?.fetchFacilities === 'function'
  ) {
    try {
      isFetchingFacilities.value = true;
      console.log('DoctorBookingsPage: Calling fetchFacilities on BillingTab with searchQuery:', searchQuery.value, 'filters:', filters.value);
      await tabComponent.value.fetchFacilities();
      console.log('DoctorBookingsPage: Facilities fetched successfully');
    } catch (error) {
      console.error('DoctorBookingsPage: Error fetching facilities:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
    } finally {
      isFetchingFacilities.value = false;
    }
  } else {
    console.warn('DoctorBookingsPage: Cannot fetch facilities:', {
      currentTab: currentTab.value,
      tabComponent: tabComponent.value,
      hasFetchFacilities: typeof tabComponent.value?.fetchFacilities === 'function',
    });
  }
}, 500);

const handleFacilitySubmit = debounce(async (responseData) => {
  console.log('DoctorBookingsPage: Facility submitted:', JSON.stringify(responseData, null, 2));
  triggerFetchFacilities();
}, 500);

const handleSpecialistsFinish = debounce(async () => {
  console.log('DoctorBookingsPage: Specialists submitted');
  triggerFetchFacilities();
  closeFacilityModal();
}, 500);

const handleEditFacility = (facility) => {
  console.log('DoctorBookingsPage: Editing facility:', JSON.stringify(facility, null, 2));
  selectedFacility.value = facility;
  facilityId.value = facility.FacilityId;
  showFacilityModal.value = true;
};

const redirectProfilePage = () => {
  console.log('DoctorBookingsPage: Redirecting to profile page');
  router.push('/profile');
};

const logout = async () => {
  console.log('DoctorBookingsPage: Logging out');
  
  // Show logout toast message
  toast(
    'You have been logged out successfully. Thank you for using Rafiki360!',
    {
      type: 'info',
      timeout: 3000,
      closeOnClick: true,
      pauseOnFocusLoss: true,
      pauseOnHover: true,
      draggable: true,
      draggablePercent: 0.6,
      showCloseButtonOnHover: false,
      hideProgressBar: false,
      closeButton: "button",
      icon: true,
      rtl: false
    }
  );
  
  // Clear auth state first
  store.dispatch('logout');
  
  // Use window.location.href as fallback for more reliable navigation
  try {
    await router.push('/login');
  } catch (error) {
    console.warn('Router navigation failed, using window.location:', error);
    window.location.href = '/login';
  }
};

watch(() => route.path, () => {
  console.log('DoctorBookingsPage: Route changed, closing dropdown');
  dropdownOpen.value = false;
});

// Watch searchQuery and filters
watch(
  () => [searchQuery.value, filters.value.start_date, filters.value.end_date],
  () => {
    console.log('DoctorBookingsPage: Search or filters changed:', {
      searchQuery: searchQuery.value,
      filters: filters.value,
    });
    triggerFetchFacilities();
  },
  { deep: true }
);
</script>